import React from 'react';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Container from '@mui/material/Container';
import { gql } from '@yi/core';
import { i18n_see_all } from '@yi/i18n/phrases/i18n_see_all';

import { utilities } from '~/common';

import { BaseCarousel, BaseCarouselComposerProps } from './BaseCarousel';
import { LargeCarouselHeader, Header, HeadingText } from './BaseCarousel/elements';
import { CarouselPlaceholder } from './CarouselPlaceholder';
import { WithPlaceholder } from './types';
import { CourseCard } from '../cards';
import { useLocale } from '../LocaleProvider';

type CarouselItem = gql.typesClient.Fragment_CourseCard | gql.typesSsr.Fragment_CourseCard;
type Props = BaseCarouselComposerProps<CarouselItem> & {
  largeHeaderTitle?: string;
  largeHeaderSubtitle?: string;
  hideHeading?: boolean;
};

const SeeAllText = () => {
  const { locale } = useLocale();
  return <>{i18n_see_all(locale)}</>;
};

const LargeHeader = function (props: Props) {
  const defaultButton = {
    text: <SeeAllText />,
    onClick: props.onHeaderClick,
  };
  const {
    largeHeaderTitle,
    largeHeaderSubtitle,
    hideHeading,
    onHeaderClick,
    heading,
    button = defaultButton,
  } = props;
  if (!largeHeaderTitle) return null;
  return (
    <Container maxWidth="xl">
      <LargeCarouselHeader title={largeHeaderTitle || ''} subtitle={largeHeaderSubtitle} />
      <Header
        style={{ justifyContent: hideHeading ? 'flex-end' : 'space-between', marginBottom: -5 }}
      >
        {!hideHeading && (
          <HeadingText
            onClick={onHeaderClick}
            role="button"
            data-testid={`app-slider-header-${utilities.formatKebabCase(heading)}`}
            aria-label={`View all ${heading}`}
          >
            {heading}
          </HeadingText>
        )}
        {button && (
          <HeadingText
            style={{
              minWidth: '150px',
              textAlign: 'right',
              justifyContent: 'flex-end',
            }}
            onClick={button.onClick}
            data-testid="app-slider-button"
          >
            {button.text}
            <ChevronRight />
          </HeadingText>
        )}
      </Header>
    </Container>
  );
};

type PlaceholderProps = Pick<Props, 'button' | 'headerCSS' | 'heading'>;
const Placeholder: React.FC<PlaceholderProps> = ({ button, ...props }) => (
  <CarouselPlaceholder
    headerDimensions={{ headerHeight: 15, headerWidth: 230 }}
    cardDimension={CourseCard.dimension}
    noButton={button === null}
    data-test-percy-hidden
    {...props}
  />
);

export const CourseCarousel: React.FC<Props> & WithPlaceholder<PlaceholderProps> = props => {
  const { heading } = props;
  const { locale } = useLocale();
  const hideHeading = locale === 'en' && props.largeHeaderTitle;
  if (!props.items?.length) return null;
  return (
    <>
      {props.items.length > 0 && locale === 'en' && <LargeHeader {...props} />}
      <BaseCarousel<CarouselItem>
        renderCard={cardProps => <CourseCard {...cardProps} />}
        getReactKey={c => c.title}
        {...props}
        heading={hideHeading ? '' : heading}
      />
    </>
  );
};

CourseCarousel.Placeholder = Placeholder;
